/**
 * Draggable preset group header (moves whole group on drag)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';

import { Button, Checkbox, Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface SortableGroupHeaderRowProps {
  id: string;
  label: string;
  templateDropCount: number;
  presetCount: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onApplyGroup?: () => void;
  applyDisabled?: boolean;
  applyGroupTooltip?: string;
  bulkSelection?: boolean;
  groupBulkChecked?: boolean | 'indeterminate';
  onToggleGroupBulk?: () => void;
}

export const SortableGroupHeaderRow: FC<SortableGroupHeaderRowProps> = ({
  id,
  label,
  templateDropCount,
  presetCount,
  expanded,
  onToggleExpand,
  onApplyGroup,
  applyDisabled = false,
  applyGroupTooltip = 'Create prepared drops for every preset in this group',
  bulkSelection = false,
  groupBulkChecked = false,
  onToggleGroupBulk,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3',
        isDragging && 'invisible',
      )}
    >
      <button
        type="button"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse group' : 'Expand group'}
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      <button
        type="button"
        title="Drag to move entire group"
        className="flex h-8 w-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-md border border-border/40 bg-background/60 text-muted-foreground hover:bg-muted/50 active:cursor-grabbing"
        aria-label="Drag to reorder group"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {bulkSelection ? (
        <Checkbox
          className="shrink-0"
          checked={groupBulkChecked}
          onCheckedChange={() => onToggleGroupBulk?.()}
          aria-label={`Select or deselect all presets in ${label}`}
          onClick={(e) => e.stopPropagation()}
        />
      ) : null}
      <button
        type="button"
        className="min-w-0 flex-1 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-muted/30"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
      >
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="mt-0.5 block text-xs tabular-nums text-muted-foreground">
          {templateDropCount} drop{templateDropCount !== 1 ? 's' : ''}
          <span className="mx-1">·</span>
          {presetCount} preset{presetCount !== 1 ? 's' : ''}
        </span>
      </button>
      {onApplyGroup != null && (
        <Tooltip content={applyGroupTooltip} side="top">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground"
            disabled={applyDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onApplyGroup();
            }}
          >
            Apply all
          </Button>
        </Tooltip>
      )}
    </div>
  );
};
